import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Switch,
    Tooltip,
} from '@mui/material';
import { ArrowDropDown, Save } from '@mui/icons-material';
import chartingMenuSx from './ChartingMenu.styles';
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { actions } from '../Chart.redux';

interface MenuSaveProps {
    saving: boolean;
    autoSaveEnabled: boolean;
    autoSaveInterval: number;
    autoSaveProgress: number;
    onSave: () => void;
}

const MenuSave = ({ saving, autoSaveEnabled, autoSaveInterval, autoSaveProgress, onSave }: MenuSaveProps) => {
    const theme = useTheme();
    const sx = chartingMenuSx(theme);
    const dispatch = useDispatch<any>();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const autosaveIntervalText = useMemo(() => {
        if (autoSaveInterval < 60) {
            return `${autoSaveInterval} ${autoSaveInterval === 1 ? 'second' : 'seconds'}`;
        }

        const minutes = Math.floor(autoSaveInterval / 60);
        const seconds = autoSaveInterval % 60;
        const minuteText = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

        if (seconds === 0) {
            return minuteText;
        }

        return `${minuteText}, ${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    }, [autoSaveInterval]);

    const handleToggleAutoSave = () => {
        const enabledDisabled = autoSaveEnabled ? 'disabled' : `enabled (saves every ${autosaveIntervalText})`;

        enqueueSnackbar(`Auto-save ${enabledDisabled}.`, {
            variant: autoSaveEnabled ? 'default' : 'success',
            autoHideDuration: autoSaveEnabled ? 1000 : 2000,
        });

        dispatch(actions.setState({ autoSaveEnabled: !autoSaveEnabled }));
    };

    const handleSave = () => {
        onSave();
        dispatch(actions.saveChart());
    };

    return (
        <>
            <ButtonGroup variant="outlined" sx={sx.saveButtonGroup}>
                <Button disabled={saving} onClick={handleSave}>
                    <Box component="span" sx={sx.saveLeadingSlot}>
                        {autoSaveEnabled ? (
                            <Tooltip title="Autosave: When the circle is full, your chart will be automatically saved.">
                                <CircularProgress
                                    enableTrackSlot
                                    size={16}
                                    variant="determinate"
                                    value={autoSaveProgress}
                                    sx={sx.saveProgress}
                                />
                            </Tooltip>
                        ) : (
                            <Save sx={sx.saveProgress} />
                        )}
                    </Box>
                    Save
                </Button>
                <Button sx={sx.buttonSmall} aria-label="save options" onClick={(e) => setAnchor(e.currentTarget)}>
                    <ArrowDropDown />
                </Button>
            </ButtonGroup>

            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                <MenuList dense>
                    <MenuItem onClick={handleToggleAutoSave}>
                        <ListItemText
                            primary="Autosave"
                            secondary={autoSaveEnabled ? `On (${autosaveIntervalText})` : 'Off'}
                        />
                        <Switch
                            edge="end"
                            checked={autoSaveEnabled}
                            onChange={handleToggleAutoSave}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </MenuItem>
                </MenuList>
            </Menu>
        </>
    );
};

export default MenuSave;
